import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, addDoc, doc, getDoc, deleteDoc, serverTimestamp, writeBatch, onSnapshot } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { Laptop, Key, Info, Calendar, Clock, CheckCircle, XCircle, ChevronDown, ChevronRight, X, AlertCircle, Search, RefreshCw, Trash2, Box } from 'lucide-react';
import { formatDateTime } from '../utils/dateFormatter';
import { format } from 'date-fns';
import { useOutletContext } from 'react-router-dom';
import HeaderActionsPortal from './HeaderActionsPortal';
import { useT } from '../i18n.jsx';

const MDMUser = () => {
  const { t } = useT();
  // displayStatus is compared against its English word for the colours and icons, so only the
  // shown word goes through the dictionary, with the stored value as the fallback.
  const statusWord = (v) => t(
    `mdmUser.statuses.${String(v || '').toLowerCase().replace(/[^a-z]/g, '')}`, {}, v,
  );
  const [activeTab, setActiveTab] = useState('available');
  const [assets, setAssets] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  const [upcomingExpanded, setUpcomingExpanded] = useState(true);
  const [pastExpanded, setPastExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Booking Modal State
  const [bookingModal, setBookingModal] = useState({ open: false, asset: null, bookings: [], timetableSlots: [] });
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [showFullDay, setShowFullDay] = useState(false);
  const [bookingForm, setBookingForm] = useState({ quantity: 1 });
  const [availableQty, setAvailableQty] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());

  const currentWeekStart = new Date(currentDate);
  currentWeekStart.setDate(currentDate.getDate() - currentDate.getDay());
  currentWeekStart.setHours(0, 0, 0, 0);

  const user = auth.currentUser;
  const { activeSchoolId } = useOutletContext();

  useEffect(() => {
    if (!activeSchoolId) return;
    setLoading(true);
    
    const unsubscribeAssets = onSnapshot(query(collection(db, 'schools', activeSchoolId, 'mdm_assets')), (snap) => {
      const assetList = [];
      snap.forEach(d => {
        assetList.push({ id: d.id, ...d.data() });
      });
      setAssets(assetList);
      setLoading(false);
    }, (error) => {
      console.error(error);
      setLoading(false);
    });

    let unsubscribeBookings = () => {};
    if (user?.email) {
      let accountId = user.email.toLowerCase();
      getDoc(doc(db, 'schools', activeSchoolId, 'email_index', accountId)).then(indexDoc => {
        if (indexDoc.exists() && indexDoc.data().accountId) {
          accountId = indexDoc.data().accountId;
        }
        // Filtered on userEmail, not userId: userId holds an accountId, which a security rule
        // cannot check against the caller. The rule tests userEmail, and a list query is only
        // allowed if it filters on the same field.
        const q = query(collection(db, 'schools', activeSchoolId, 'mdm_bookings'), where('userEmail', '==', (user.email || '').toLowerCase()));
        unsubscribeBookings = onSnapshot(q, (snap) => {
          const bList = [];
          snap.forEach(d => bList.push({ id: d.id, ...d.data() }));
          bList.sort((a, b) => (b.requestDate?.seconds || 0) - (a.requestDate?.seconds || 0));
          setMyBookings(bList);
        });
      });
    }

    return () => {
      unsubscribeAssets();
      unsubscribeBookings();
    };
  }, [user, activeSchoolId]);

  const openBookingModal = async (asset) => {
    setBookingModal({ open: true, asset, bookings: [], timetableSlots: [] });
    setSelectedSlots([]);
    setBookingForm({ quantity: 1 });
    setAvailableQty(asset.quantity || 1);
    
    // Fetch bookings for this asset to calculate overlapping availability
    const bSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'mdm_bookings'), where('assetId', '==', asset.id), where('status', 'in', ['Approved', 'Pending'])));
    const bList = [];
    bSnap.forEach(d => {
       const data = d.data();
       if (data.startDate && data.endDate) {
          bList.push({
             ...data,
             startDate: data.startDate.toDate ? data.startDate.toDate() : new Date(data.startDate),
             endDate: data.endDate.toDate ? data.endDate.toDate() : new Date(data.endDate),
          });
       }
    });

    // Fetch timetable profiles and calendar events
    const pSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'timetable_profiles')));
    const profiles = {};
    let defaultProfile = null;
    pSnap.forEach(d => {
      profiles[d.id] = d.data();
      if (d.data().isDefault) defaultProfile = d.data();
    });

    const cSnap = await getDocs(query(collection(db, 'schools', activeSchoolId, 'calendar_events')));
    const cList = [];
    cSnap.forEach(d => cList.push(d.data()));

    const generatedSlots = [];
    cList.forEach(day => {
      let profile = null;
      if (day.timetableProfileId && day.timetableProfileId !== 'default' && profiles[day.timetableProfileId]) {
        profile = profiles[day.timetableProfileId];
      } else if (day.type === 'School Day') {
        profile = defaultProfile;
      }
      
      if (profile && profile.periods) {
         profile.periods.forEach((period, idx) => {
            const startStr = `${day.date}T${period.startTime}:00`;
            const endStr = `${day.date}T${period.endTime}:00`;
            const startDate = new Date(startStr);
            const endDate = new Date(endStr);
            
            let current = 0;
            let peak = 0;
            let events = [];
            bList.forEach(b => {
               if (b.endDate > startDate && b.startDate < endDate) {
                  events.push({ time: b.startDate < startDate ? startDate.getTime() : b.startDate.getTime(), val: b.quantity || 1 });
                  events.push({ time: b.endDate > endDate ? endDate.getTime() : b.endDate.getTime(), val: -(b.quantity || 1) });
               }
            });
            events.sort((a,b) => a.time - b.time || a.val - b.val);
            for (let e of events) {
               current += e.val;
               if (current > peak) peak = current;
            }
            
            const slotAvail = Math.max(0, (asset.quantity || 1) - peak);

            generatedSlots.push({
               id: `${day.date}_${idx}`,
               title: `${period.name}`,
               start: startDate,
               end: endDate,
               availableQty: slotAvail,
               type: period.type
            });
         });
      }
    });

      setBookingModal(prev => ({ 
        ...prev, 
        timetableSlots: generatedSlots,
        timetableProfiles: profiles,
        calendarEvents: cList,
        bookings: bList
      }));
  };



  const handleQuantityChange = (qty) => {
    let parsedQty = qty === '' ? 0 : parseInt(qty, 10);
    if (isNaN(parsedQty)) parsedQty = 0;
    setBookingForm({...bookingForm, quantity: parsedQty});
  };

  const handleCellClick = (cellSlot) => {
    if (cellSlot.start < new Date()) {
       alert(t('mdmUser.cannotBookPast'));
       return;
    }
    if (cellSlot.availableQty < bookingForm.quantity) {
       alert(t('mdmUser.notEnoughQuantity', { max: cellSlot.availableQty }));
       return;
    }
    const isSelected = selectedSlots.some(s => s.id === cellSlot.id);
    if (isSelected) {
       setSelectedSlots(selectedSlots.filter(s => s.id !== cellSlot.id));
    } else {
       setSelectedSlots([...selectedSlots, {
          ...cellSlot,
          customStart: cellSlot.start,
          customEnd: cellSlot.end
       }]);
    }
  };

  const getPeakUsage = (start, end) => {
    let events = [];
    bookingModal.bookings.forEach(b => {
       const bStart = b.startDate.toDate ? b.startDate.toDate() : new Date(b.startDate);
       const bEnd = b.endDate.toDate ? b.endDate.toDate() : new Date(b.endDate);
       if (bEnd > start && bStart < end) {
          events.push({ time: bStart < start ? start.getTime() : bStart.getTime(), val: b.quantity || 1 });
          events.push({ time: bEnd > end ? end.getTime() : bEnd.getTime(), val: -(b.quantity || 1) });
       }
    });
    events.sort((a,b) => a.time - b.time || a.val - b.val);
    let current = 0;
    let peak = 0;
    for (let e of events) {
       current += e.val;
       if (current > peak) peak = current;
    }
    return peak;
  };

  const isSlotValid = (slot) => {
     if (slot.customStart >= slot.customEnd) return false;
     const peak = getPeakUsage(slot.customStart, slot.customEnd);
     return (bookingModal.asset?.quantity || 1) - peak >= bookingForm.quantity;
  };

  const handleCustomTimeChange = (slotId, type, timeStr) => {
     setSelectedSlots(prev => prev.map(s => {
        if (s.id !== slotId) return s;
        const [hours, minutes] = timeStr.split(':').map(Number);
        const newDate = new Date(type === 'start' ? s.customStart : s.customEnd);
        newDate.setHours(hours, minutes, 0, 0);
        return {
           ...s,
           [type === 'start' ? 'customStart' : 'customEnd']: newDate
        };
     }));
  };

  const formatMin = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  const generateMasterRows = () => {
    const profiles = Object.values(bookingModal.timetableProfiles || {});
    let masterProfile = null;
    let maxPeriods = 0;
    profiles.forEach(p => {
       if (p.periods && p.periods.length > maxPeriods) {
          maxPeriods = p.periods.length;
          masterProfile = p;
       }
    });

    const startHour = showFullDay ? 0 : 7;
    const endHour = showFullDay ? 24 : 19;
    
    const masterRows = [];
    const getMin = (timeStr) => {
       const [h, m] = timeStr.split(':').map(Number);
       return h * 60 + m;
    };

    let periodDefs = [];
    if (masterProfile && masterProfile.periods) {
       periodDefs = masterProfile.periods.map(p => ({
          title: p.name,
          startMin: getMin(p.startTime),
          endMin: getMin(p.endTime)
       })).sort((a,b) => a.startMin - b.startMin);
    }

    let currentMin = startHour * 60;
    const endMin = endHour * 60;

    let periodIdx = 0;
    while (currentMin < endMin) {
       if (periodIdx < periodDefs.length && currentMin >= periodDefs[periodIdx].startMin) {
          const p = periodDefs[periodIdx];
          masterRows.push({ isPeriod: true, title: p.title, startMin: p.startMin, endMin: p.endMin });
          currentMin = p.endMin;
          periodIdx++;
       } else {
          let nextBlockEnd = currentMin + 30;
          if (periodIdx < periodDefs.length && nextBlockEnd > periodDefs[periodIdx].startMin) {
             nextBlockEnd = periodDefs[periodIdx].startMin;
          }
          if (nextBlockEnd > endMin) nextBlockEnd = endMin;
          if (nextBlockEnd > currentMin) {
             masterRows.push({ isPeriod: false, title: '', startMin: currentMin, endMin: nextBlockEnd });
             currentMin = nextBlockEnd;
          } else {
             currentMin += 30;
          }
       }
    }
    
    if (!showFullDay) {
       return masterRows.filter(r => r.isPeriod);
    }

    return masterRows;
  };

  const getDayLabel = (date) => {
    const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const dayStr = t(`mdmUser.dayLetters.${days[date.getDay()]}`);
    
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const event = (bookingModal.calendarEvents || []).find(e => e.date === dateStr);
    
    let cycleStr = '';
    if (event && event.cycleDay) {
       const cDay = String(event.cycleDay).replace('Day ', '').trim();
       const standardDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
       if (!standardDays.includes(cDay)) {
          cycleStr = `/${cDay}`;
       }
    }
    
    return `${date.getDate()}/${date.getMonth() + 1} (${dayStr}${cycleStr})`;
  };

  const getCellSlot = (dateStr, row) => {
     const [year, month, day] = dateStr.split('-').map(Number);
     const start = new Date(year, month - 1, day, Math.floor(row.startMin / 60), row.startMin % 60);
     const end = new Date(year, month - 1, day, Math.floor(row.endMin / 60), row.endMin % 60);
     const id = `${dateStr}_${row.startMin}`;
     
     let usedQty = 0;
     const cellBookings = [];
     (bookingModal.bookings || []).forEach(b => {
        const bStart = new Date(b.startDate);
        const bEnd = new Date(b.endDate);
        // Overlap logic
        if (bStart < end && bEnd > start) {
           usedQty += Number(b.quantity || 1);
           cellBookings.push(b);
        }
     });

     const availableQty = (bookingModal.asset?.quantity || 0) - usedQty;

     return { id, title: row.title, start, end, isPeriod: row.isPeriod, availableQty, usedQty, cellBookings };
  };

  const submitBooking = async (e) => {
    e.preventDefault();
    const asset = bookingModal.asset;
    const isAutoApprove = asset.autoApprove === true;
    const initialStatus = isAutoApprove ? 'Reserved' : 'Pending';

    try {
      if (selectedSlots.length === 0) {
         alert(t('mdmUser.selectOneSlot'));
         return;
      }
      
      const allValid = selectedSlots.every(isSlotValid);
      if (!allValid) {
         alert(t('mdmUser.exceedsQuantity'));
         return;
      }
      
      const batch = writeBatch(db);
      let accountId = user.email.toLowerCase();
      const indexDoc = await getDoc(doc(db, 'schools', activeSchoolId, 'email_index', accountId));
      if (indexDoc.exists() && indexDoc.data().accountId) {
        accountId = indexDoc.data().accountId;
      }
      
      // Merge consecutive slots on the same day
      const mergedSlots = [];
      const slotsByDay = {};

      selectedSlots.forEach(slot => {
        const dateKey = `${slot.customStart.getFullYear()}-${slot.customStart.getMonth()}-${slot.customStart.getDate()}`;
        if (!slotsByDay[dateKey]) slotsByDay[dateKey] = [];
        slotsByDay[dateKey].push(slot);
      });

      Object.values(slotsByDay).forEach(daySlots => {
        daySlots.sort((a, b) => a.customStart.getTime() - b.customStart.getTime());
        
        let currentMerged = null;
        for (const slot of daySlots) {
          if (!currentMerged) {
            currentMerged = { customStart: slot.customStart, customEnd: slot.customEnd };
          } else {
            if (currentMerged.customEnd.getTime() === slot.customStart.getTime()) {
              currentMerged.customEnd = slot.customEnd;
            } else {
              mergedSlots.push(currentMerged);
              currentMerged = { customStart: slot.customStart, customEnd: slot.customEnd };
            }
          }
        }
        if (currentMerged) {
          mergedSlots.push(currentMerged);
        }
      });

      mergedSlots.forEach(slot => {
        const newDocRef = doc(collection(db, 'schools', activeSchoolId, 'mdm_bookings'));
        batch.set(newDocRef, {
          assetId: bookingModal.asset.id,
          assetName: bookingModal.asset.name,
          schoolId: activeSchoolId,
          userId: accountId,
          // The rule pins creation to this field. userId holds an accountId resolved through
          // email_index, which rules cannot verify against request.auth and anyone can look up.
          userEmail: (user.email || '').toLowerCase(),
          userName: user.displayName || user.email,
          quantity: Number(bookingForm.quantity),
          startDate: slot.customStart,
          endDate: slot.customEnd,
          status: initialStatus,
          requestDate: serverTimestamp() // Ensure requestDate matches the query sort field
        });
      });
      await batch.commit();

      alert(isAutoApprove ? t('mdmUser.borrowSuccess') : t('mdmUser.requestSubmitted'));
      setBookingModal({ open: false, asset: null, bookings: [], timetableSlots: [] });
    } catch(e) {
      alert(e.message);
    }
  };

  const handleDeleteBooking = async (id) => {
    if (window.confirm(t('mdmUser.cancelConfirm'))) {
      try {
        await deleteDoc(doc(db, 'schools', activeSchoolId, 'mdm_bookings', id));
      } catch(e) {
        alert(t('mdmUser.cancelFailed', { message: e.message }));
      }
    }
  };

  const filteredAssets = assets.filter(a => a.name.toLowerCase().includes(searchTerm.toLowerCase()));

  const getDateOnly = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const nowForSort = new Date();
  const comingBookings = [];
  const pastBookings = [];
  
  myBookings.forEach(b => {
     const bStart = b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate);
     const bEnd = b.endDate?.toDate ? b.endDate.toDate() : new Date(b.endDate);
     if (bEnd >= nowForSort) {
        comingBookings.push(b);
     } else {
        pastBookings.push(b);
     }
  });
  
  comingBookings.sort((a, b) => {
     const aStart = a.startDate?.toDate ? a.startDate.toDate() : new Date(a.startDate);
     const bStart = b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate);
     const aDate = getDateOnly(aStart);
     const bDate = getDateOnly(bStart);
     if (aDate !== bDate) return aDate - bDate; // Date ASC
     return bStart.getTime() - aStart.getTime(); // Time DESC
  });
  
  pastBookings.sort((a, b) => {
     const aStart = a.startDate?.toDate ? a.startDate.toDate() : new Date(a.startDate);
     const bStart = b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate);
     const aDate = getDateOnly(aStart);
     const bDate = getDateOnly(bStart);
     if (aDate !== bDate) return bDate - aDate; // Date DESC
     return bStart.getTime() - aStart.getTime(); // Time DESC
  });

  const renderBookingCard = (b) => {
    let displayStatus = b.status;
    // Map Approved to Reserved for backwards compatibility and logic
    if (displayStatus === 'Approved') displayStatus = 'Reserved';
    
    // Check if completed
    const end = b.endDate?.toDate ? b.endDate.toDate() : new Date(b.endDate);
    if (displayStatus === 'Reserved' && end < new Date()) {
      displayStatus = 'Completed';
    }

    return (
    <div key={b.id} style={{ padding: '1rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: '0.875rem' }}>
      <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>{b.assetName} x{b.quantity || 1}</div>
      <div style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem', marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <Clock size={12} /> {t('mdmUser.requested')} {b.requestDate?.toDate ? formatDateTime(b.requestDate.toDate()) : b.requestDate ? formatDateTime(new Date(b.requestDate)) : t('mdmUser.justNow')}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
          <Calendar size={12} /> {b.startDate?.toDate ? formatDateTime(b.startDate.toDate()) : b.startDate ? formatDateTime(new Date(b.startDate)) : '?'} - {b.endDate?.toDate ? formatDateTime(b.endDate.toDate()) : b.endDate ? formatDateTime(new Date(b.endDate)) : '?'}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ 
          display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.2rem 0.5rem', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600',
          backgroundColor: displayStatus === 'Reserved' ? 'var(--color-success-bg)' : displayStatus === 'Completed' ? 'var(--color-info-bg)' : displayStatus === 'Pending' ? 'var(--color-warning-bg)' : displayStatus === 'Returned' ? 'var(--color-bg-subtle)' : 'var(--color-danger-bg)',
          color: displayStatus === 'Reserved' ? 'var(--color-success-text)' : displayStatus === 'Completed' ? 'var(--color-info-text)' : displayStatus === 'Pending' ? 'var(--color-warning-text)' : displayStatus === 'Returned' ? 'var(--color-text-subtle)' : 'var(--color-danger-text)'
        }}>
          {displayStatus === 'Reserved' && <CheckCircle size={12} />}
          {displayStatus === 'Completed' && <CheckCircle size={12} />}
          {displayStatus === 'Pending' && <Clock size={12} />}
          {displayStatus === 'Returned' && <CheckCircle size={12} />}
          {displayStatus === 'Rejected' && <XCircle size={12} />}
          {statusWord(displayStatus)}
        </div>
        
        {new Date() < (b.startDate?.toDate ? b.startDate.toDate() : new Date(b.startDate)) && (
          <button type="button" onClick={() => handleDeleteBooking(b.id)} style={{ background: 'none', border: 'none', color: 'var(--color-danger-text)', fontSize: '0.75rem', fontWeight: '600', cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
             {t('mdmUser.cancelBooking')}
          </button>
        )}
      </div>
    </div>
    );
  };

  return (
    <div className="animate-fade-in new-theme" style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      
      {/* Top Tabs */}
      <div className="theme-tabs-bar">
        <div className="theme-tabs-group">
          {[
            { id: 'available', label: t('mdmUser.tabs.devices'), icon: Laptop },
            { id: 'my-loans', label: t('mdmUser.tabs.myBookings'), icon: Box }
          ].map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`theme-tab ${isActive ? 'active' : ''}`}
              >
                <div className="theme-tab-icon">
                  <Icon size={20} />
                </div>
                {tab.label}
              </button>
            );
          })}
        </div>
        {activeTab === 'available' && (
          <HeaderActionsPortal>
            <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
              <Search size={16} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-muted)' }} />
              <input
                type="text"
                placeholder={t('mdmUser.searchAssets')}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{ padding: '0.4rem 1rem 0.4rem 2.2rem', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', outline: 'none', minWidth: '250px', fontSize: '0.875rem' }}
              />
            </div>
          </HeaderActionsPortal>
        )}
      </div>

      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>

        {/* Available Assets */}
        <div className="theme-card-container" style={{ display: activeTab === 'available' ? 'flex' : 'none' }}>
          <div className="theme-card-content">
            {loading ? (
              <div className="text-muted text-center py-4">{t('mdmUser.loading')}</div>
            ) : filteredAssets.length === 0 ? (
              <div className="text-muted text-center py-4">{t('mdmUser.noAssets')}</div>
            ) : (
              <div className="theme-grid">
                {filteredAssets.map(asset => (
                  <div key={asset.id} className="theme-item-card">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <Laptop size={18} color="var(--color-primary)" />
                      <span style={{ fontWeight: '600' }}>{asset.name}</span>
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '1rem' }}>
                      {t('mdmUser.type')} {asset.type} <br/>
                      {t('mdmUser.approval')} {asset.autoApprove ? t('mdmUser.approvalAuto') : t('mdmUser.approvalManual')}
                    </div>
                    <button 
                      onClick={() => openBookingModal(asset)}
                      className="btn-primary" 
                      style={{ marginTop: 'auto', padding: '0.4rem', fontSize: '0.875rem' }}
                    >
                      {t('mdmUser.requestBorrow')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* My Bookings Sidebar */}
        <div className="theme-card-container" style={{ display: activeTab === 'my-loans' ? 'flex' : 'none' }}>

          <div className="theme-card-content">
            {myBookings.length === 0 ? (
              <div className="text-muted text-center py-4 small">{t('mdmUser.noHistory')}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {comingBookings.length > 0 && (
                   <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                     <div 
                        onClick={() => setUpcomingExpanded(!upcomingExpanded)}
                        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}
                     >
                        {upcomingExpanded ? <ChevronDown size={16} style={{ marginRight: '0.5rem', color: 'var(--color-primary)' }} /> : <ChevronRight size={16} style={{ marginRight: '0.5rem', color: 'var(--color-primary)' }} />}
                        <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: 'var(--color-primary)' }}>{t('mdmUser.upcoming')}</h4>
                     </div>
                     {upcomingExpanded && comingBookings.map(renderBookingCard)}
                   </div>
                )}
                {pastBookings.length > 0 && (
                   <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                     <div 
                        onClick={() => setPastExpanded(!pastExpanded)}
                        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', borderBottom: '1px solid var(--color-border)', paddingBottom: '0.5rem' }}
                     >
                        {pastExpanded ? <ChevronDown size={16} style={{ marginRight: '0.5rem', color: 'var(--color-text-muted)' }} /> : <ChevronRight size={16} style={{ marginRight: '0.5rem', color: 'var(--color-text-muted)' }} />}
                        <h4 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: 'var(--color-text-muted)' }}>{t('mdmUser.past')}</h4>
                     </div>
                     {pastExpanded && pastBookings.map(renderBookingCard)}
                   </div>
                )}
              </div>
            )}
          </div>
        </div>

      </div>

      {/* Booking Modal */}
      {bookingModal.open && bookingModal.asset && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'var(--color-overlay)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ backgroundColor: 'var(--color-bg-card)', padding: '2rem', borderRadius: 'var(--radius-lg)', width: '95%', maxWidth: '1400px', maxHeight: '95vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid var(--border-color)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '2rem' }}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: '600', margin: 0 }}>{t('mdmUser.borrowTitle', { name: bookingModal.asset.name })}</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>

                  <label style={{ fontSize: '0.875rem', fontWeight: '600', margin: 0, marginRight: '0.25rem' }}>{t('mdmUser.requestedQuantity')}</label>
                  <button type="button" className="btn-outline" onClick={() => handleQuantityChange(Math.max(1, bookingForm.quantity - 1))} style={{ padding: '0.2rem 0.5rem' }}>-</button>
                  <input type="number" className="input-field" style={{ width: '50px', padding: '0.3rem', margin: 0, textAlign: 'center' }} value={bookingForm.quantity} onChange={e => handleQuantityChange(e.target.value)} min="1" max={bookingModal.asset.quantity || 1} required />
                  <button type="button" className="btn-outline" onClick={() => handleQuantityChange(Math.min(bookingModal.asset.quantity || 1, bookingForm.quantity + 1))} style={{ padding: '0.2rem 0.5rem' }}>+</button>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginRight: '1rem' }}>
                  <div style={{ fontSize: '0.875rem', fontWeight: '600', color: 'var(--color-primary)', whiteSpace: 'nowrap' }}>
                    {t('mdmUser.slotsSelected', { count: selectedSlots.length })}
                  </div>
                  <button type="button" className="btn-outline" onClick={() => setShowFullDay(!showFullDay)} style={{ fontSize: '0.75rem', padding: '0.3rem 0.6rem', whiteSpace: 'nowrap' }}>
                    {showFullDay ? t('mdmUser.showTimetableOnly') : t('mdmUser.showFullDay')}
                  </button>
                </div>
              </div>
            </div>
            <>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '1rem' }}>
                   <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <button className="btn-outline" onClick={() => setCurrentDate(new Date())} style={{ padding: '0.3rem 0.6rem', fontSize: '0.875rem' }}>{t('mdmUser.today')}</button>
                      <button className="btn-outline" onClick={() => {
                         const d = new Date(currentDate);
                         d.setDate(d.getDate() - 7);
                         setCurrentDate(d);
                      }} style={{ padding: '0.3rem 0.6rem', fontSize: '0.875rem' }}>{t('mdmUser.back')}</button>
                      <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: '600', whiteSpace: 'nowrap' }}>
                        {`${currentWeekStart.getDate()}/${currentWeekStart.getMonth() + 1}/${currentWeekStart.getFullYear()}`} - 
                        {(() => {
                           const end = new Date(currentWeekStart);
                           end.setDate(end.getDate() + 6);
                           return ` ${end.getDate()}/${end.getMonth() + 1}/${end.getFullYear()}`;
                        })()}
                      </h3>
                      <button className="btn-outline" onClick={() => {
                         const d = new Date(currentDate);
                         d.setDate(d.getDate() + 7);
                         setCurrentDate(d);
                      }} style={{ padding: '0.3rem 0.6rem', fontSize: '0.875rem' }}>{t('mdmUser.next')}</button>
                   </div>
                </div>
                
                <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '65vh', border: '1px solid var(--border-color)', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0, textAlign: 'center', fontSize: '0.875rem' }}>
                    <thead>
                      <tr>
                        <th style={{ position: 'sticky', top: 0, left: 0, backgroundColor: 'var(--color-bg-card)', zIndex: 10, borderBottom: '2px solid var(--border-color)', borderRight: '2px solid var(--border-color)', padding: '0.3rem', minWidth: '100px' }}>{t('mdmUser.time')}</th>
                        {Array.from({length: 7}).map((_, i) => {
                          const d = new Date(currentWeekStart);
                          d.setDate(d.getDate() + i);
                          return (
                            <th key={i} style={{ position: 'sticky', top: 0, backgroundColor: 'var(--color-bg-card)', zIndex: 9, borderBottom: '2px solid var(--border-color)', borderRight: '1px solid var(--border-color)', padding: '0.3rem', minWidth: '85px' }}>
                              {getDayLabel(d)}
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {generateMasterRows().map((row, rIdx) => (
                         <tr key={rIdx}>
                           <td style={{ position: 'sticky', left: 0, backgroundColor: 'var(--color-bg-card)', zIndex: 9, borderRight: '2px solid var(--border-color)', borderBottom: '1px solid var(--border-color)', padding: '0.3rem', whiteSpace: 'nowrap' }}>
                             {row.isPeriod ? <strong style={{color: 'var(--color-primary)'}}>{row.title}</strong> : <span style={{ color: 'var(--color-text-muted)' }}>{row.title || ''}</span>}
                             <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: '0.2rem' }}>{formatMin(row.startMin)} - {formatMin(row.endMin)}</div>
                           </td>
                           {Array.from({length: 7}).map((_, i) => {
                              const d = new Date(currentWeekStart);
                              d.setDate(d.getDate() + i);
                              const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
                              const cellSlot = getCellSlot(dateStr, row);
                              const isSelected = selectedSlots.some(s => s.id === cellSlot.id);
                              const isPast = cellSlot.start < new Date();
                              
                              let bg = 'transparent';
                              let color = 'inherit';
                              let cursor = 'pointer';
                              
                              if (isPast) {
                                 bg = 'var(--color-bg-subtle)';
                                 color = 'var(--color-text-muted)';
                                 cursor = 'not-allowed';
                              } else if (cellSlot.availableQty < bookingForm.quantity) {
                                 bg = 'var(--color-bg-subtle)';
                                 color = 'var(--color-text-muted)';
                                 cursor = 'not-allowed';
                              } else if (isSelected) {
                                 bg = 'var(--color-info)';
                                 color = 'white';
                              } else if (row.isPeriod) {
                                 bg = 'var(--color-success-bg-soft)';
                              }
                              
                              return (
                                 <td key={dateStr} 
                                     style={{ borderBottom: '1px solid var(--border-color)', borderRight: '1px solid var(--border-color)', padding: '0.3rem', backgroundColor: bg, color: color, cursor: cursor, position: 'relative', verticalAlign: 'middle', height: '40px' }} 
                                     onClick={() => !isPast && handleCellClick(cellSlot)}>
                                    
                                    {cellSlot.cellBookings.map((b, idx) => (
                                       <div key={idx} style={{ backgroundColor: 'var(--color-warning)', color: 'white', fontSize: '0.7rem', padding: '0.2rem', borderRadius: '4px', marginBottom: '0.2rem', textAlign: 'left' }}>
                                         <strong>x{b.quantity || 1}</strong> {b.userId.split('@')[0]}
                                       </div>
                                    ))}
                                    
                                    {isPast && cellSlot.cellBookings.length === 0 && (
                                       <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.2rem' }}>{t('mdmUser.pastSlot')}</div>
                                    )}
                                    {!isPast && cellSlot.availableQty > 0 && cellSlot.availableQty >= bookingForm.quantity && cellSlot.cellBookings.length === 0 && (
                                       <div style={{ fontSize: '0.75rem', opacity: isSelected ? 1 : 0.6, marginTop: '0.2rem' }}>
                                         {t('mdmUser.availableCount', { count: cellSlot.availableQty })}
                                       </div>
                                    )}
                                    {!isPast && cellSlot.availableQty < bookingForm.quantity && cellSlot.cellBookings.length === 0 && (
                                       <div style={{ fontSize: '0.75rem', opacity: 0.6, marginTop: '0.2rem' }}>
                                         {t('mdmUser.full')}
                                       </div>
                                    )}
                                 </td>
                              )
                           })}
                         </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {selectedSlots.length > 0 && (
                  <div style={{ marginBottom: '1.5rem', backgroundColor: 'var(--color-bg-app)', padding: '1rem', borderRadius: 'var(--radius-md)' }}>
                    <h4 style={{ margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: 'var(--color-primary)' }}>{t('mdmUser.selectedConfig')}</h4>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      {selectedSlots.map(slot => {
                        const valid = isSlotValid(slot);
                        return (
                           <div key={slot.id} style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: '1rem', alignItems: 'center', backgroundColor: 'var(--color-bg-card)', padding: '0.75rem', borderRadius: 'var(--radius-md)', border: `1px solid ${valid ? 'var(--color-border)' : 'var(--color-danger)'}` }}>
                              <div>
                                 <div style={{ fontWeight: '600', fontSize: '0.875rem' }}>{slot.title}</div>
                                 <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)' }}>{format(slot.customStart, 'MMM d, yyyy')}</div>
                                 {!valid && <div style={{ fontSize: '0.7rem', color: 'var(--color-danger)', marginTop: '0.2rem' }}>{t('mdmUser.invalidSlot')}</div>}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                 <label style={{ fontSize: '0.7rem', fontWeight: '600' }}>{t('mdmUser.startTime')}</label>
                                 <input type="time" className="input-field" style={{ padding: '0.3rem', fontSize: '0.875rem' }} value={format(slot.customStart, 'HH:mm')} onChange={e => handleCustomTimeChange(slot.id, 'start', e.target.value)} required />
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
                                 <label style={{ fontSize: '0.7rem', fontWeight: '600' }}>{t('mdmUser.endTime')}</label>
                                 <input type="time" className="input-field" style={{ padding: '0.3rem', fontSize: '0.875rem' }} value={format(slot.customEnd, 'HH:mm')} onChange={e => handleCustomTimeChange(slot.id, 'end', e.target.value)} required />
                              </div>
                              <button type="button" onClick={() => setSelectedSlots(selectedSlots.filter(s => s.id !== slot.id))} style={{ background: 'none', border: 'none', color: 'var(--color-danger)', cursor: 'pointer', padding: '0.5rem' }}>
                                 <Trash2 size={18} />
                              </button>
                           </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: '1rem' }}>
                    <button type="button" className="btn-outline" onClick={() => setBookingModal({ open: false, asset: null, bookings: [], timetableSlots: [] })}>{t('common.cancel')}</button>
                    <button type="button" className="btn-primary" onClick={submitBooking} disabled={selectedSlots.length === 0 || !selectedSlots.every(isSlotValid)}>{t('mdmUser.submitRequests', { count: selectedSlots.length })}</button>
                  </div>
                </div>
              </>

          </div>
        </div>
      )}

    </div>
  );
};

export default MDMUser;
